import React from "react";
import { Check, CheckCircle2, XCircle } from "lucide-react";

const getVideoEmbedUrl = (url) => {
    if (!url) return "";

    let embedUrl = "";
    let isTikTok = false;

    // Handle TikTok URLs
    if (url.includes("tiktok.com")) {
        // Convert TikTok URL to embed URL
        // Example: https://www.tiktok.com/@username/video/1234567890
        // to https://www.tiktok.com/embed/v2/1234567890
        const videoId = url.split("/video/")[1]?.split("?")[0];
        if (videoId) {
            embedUrl = `https://www.tiktok.com/embed/v2/${videoId}`;
            isTikTok = true;
        }
    }
    // Handle youtu.be URLs
    else if (url.includes("youtu.be")) {
        const videoId = url.split("/").pop();
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
    }
    // Handle youtube.com URLs
    else if (url.includes("youtube.com")) {
        const urlObj = new URL(url);
        const videoId = urlObj.searchParams.get("v");
        embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : "";
    } else {
        embedUrl = url;
    }

    // Append parameters if we have a valid YouTube embed URL
    if (embedUrl && embedUrl.includes("youtube.com/embed")) {
        embedUrl += "?wmode=transparent&autoplay=1&mute=1";
    }

    return { embedUrl, isTikTok };
};

export const ToolInfo = ({ tool }) => {
    return (
        <div className="space-y-12">
            {/* What is this tool */}
            <section>
                <h2 className="text-2xl font-semibold mb-4">
                    What is {tool.name}?
                </h2>
                <p className="text-gray-600 leading-relaxed">
                    {tool.description}
                </p>
            </section>

            {/* How to use */}
            <section>
                <h2 className="text-2xl font-semibold mb-4">How to Use</h2>
                <div className="space-y-4">
                    {tool.howToUse && tool.howToUse.length > 0 ? (
                        tool.howToUse.map((step, index) => (
                            <div key={index} className="flex items-start gap-3">
                                <div className="mt-1">
                                    <Check className="h-5 w-5 text-green-500" />
                                </div>
                                <p className="text-gray-600">{step}</p>
                            </div>
                        ))
                    ) : (
                        <p className="text-gray-500">
                            No usage instructions available
                        </p>
                    )}
                </div>
            </section>

            {/* Pros and Cons */}
            {(tool.pros?.length > 0 || tool.cons?.length > 0) && (
                <section>
                    <h2 className="text-2xl font-semibold mb-4">Pros & Cons</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {tool.pros && tool.pros.length > 0 && (
                            <div>
                                <h3 className="font-medium text-green-600 mb-3">
                                    Pros
                                </h3>
                                <ul className="space-y-2">
                                    {tool.pros.map((pro, index) => (
                                        <li
                                            key={index}
                                            className="flex items-start gap-2"
                                        >
                                            <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                                            <span className="text-gray-700">
                                                {pro}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {tool.cons && tool.cons.length > 0 && (
                            <div>
                                <h3 className="font-medium text-red-600 mb-3">
                                    Cons
                                </h3>
                                <ul className="space-y-2">
                                    {tool.cons.map((con, index) => (
                                        <li
                                            key={index}
                                            className="flex items-start gap-2"
                                        >
                                            <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                                            <span className="text-gray-700">
                                                {con}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Video */}
            {tool.video?.url && (
                <section>
                    <h2 className="text-2xl font-semibold mb-4">Video</h2>
                    {(() => {
                        const { embedUrl, isTikTok } = getVideoEmbedUrl(
                            tool.video.url
                        );
                        return (
                            <div>
                                <div
                                    className="relative w-full"
                                    style={{
                                        paddingBottom: "40%",
                                        maxWidth: isTikTok ? "300px" : "100%",
                                        margin: isTikTok ? "0 auto" : "0",
                                    }}
                                >
                                    <iframe
                                        src={embedUrl}
                                        title={`${tool.name} video`}
                                        className="absolute top-0 left-0 w-full h-full rounded-lg"
                                        style={{
                                            maxHeight: isTikTok
                                                ? "600px"
                                                : "500px",
                                            objectFit: "contain",
                                        }}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                    />
                                </div>
                            </div>
                        );
                    })()}
                </section>
            )}

            {/* Core Features */}
            <section>
                <h2 className="text-2xl font-semibold mb-4">Core Features</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                    {tool.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-3">
                            <Check className="h-5 w-5 text-green-500" />
                            <span className="text-gray-600">{feature}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Use Cases */}
            <section>
                <h2 className="text-2xl font-semibold mb-4">Use Cases</h2>
                <div className="grid sm:grid-cols-2 gap-6">
                    {tool.useCases && tool.useCases.length > 0 ? (
                        tool.useCases.map((useCase, index) => (
                            <div
                                key={index}
                                className="bg-gray-50 p-4 rounded-lg"
                            >
                                {typeof useCase === "string" ? (
                                    <p className="text-gray-600">{useCase}</p>
                                ) : (
                                    <>
                                        <h3 className="font-medium mb-2">
                                            {useCase.title}
                                        </h3>
                                        <p className="text-gray-600 text-sm">
                                            {useCase.description}
                                        </p>
                                    </>
                                )}
                            </div>
                        ))
                    ) : (
                        <p className="text-gray-500">No use cases available</p>
                    )}
                </div>
            </section>

            {/* FAQs */}
            <section>
                <h2 className="text-2xl font-semibold mb-4">
                    Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                    {tool.faq && tool.faq.length > 0 ? (
                        tool.faq.map((faq, index) => (
                            <div
                                key={index}
                                className="border-b border-gray-200 pb-4"
                            >
                                <h3 className="font-medium mb-2">
                                    {faq.question}
                                </h3>
                                <p className="text-gray-600">{faq.answer}</p>
                            </div>
                        ))
                    ) : (
                        <p className="text-gray-500">No FAQs available</p>
                    )}
                </div>
            </section>
        </div>
    );
};
